import type {
  MembershipRead,
  TenantWorkspaceRead,
} from "./tenantContext.types";

/**
 * Offline fixtures for the post-login tenant selection flow, shaped exactly
 * like `GET /api/v2/auth/tenants` and `GET /api/v2/tenants/{id}/workspaces`
 * so switching to the live endpoints needs no call-site change.
 *
 * Scenarios (REACT_APP_TENANT_CONTEXT_SCENARIO):
 *   default  — three tenants covering happy path, single workspace, and a
 *              tenant the caller can reach but holds no workspace in
 *   empty    — no tenant memberships at all (the platform/super-admin case)
 *   error    — both endpoints fail, to exercise the error surfaces
 */
export type TenantContextScenario = "default" | "empty" | "error";

/** Real network is never instant; without this the loading states are invisible. */
export const MOCK_TENANT_CONTEXT_LATENCY_MS = 400;

export const MOCK_TENANT_MEMBERSHIPS: MembershipRead[] = [
  {
    membership_id: "11111111-1111-4111-8111-000000000001",
    tenant_id: "aaaaaaaa-1111-4111-8111-000000000001",
    tenant_name: "Acme Analytics",
    role_name: "member",
    permissions: [],
  },
  {
    membership_id: "11111111-1111-4111-8111-000000000002",
    tenant_id: "aaaaaaaa-1111-4111-8111-000000000002",
    tenant_name: "Globex Data",
    role_name: "member",
    permissions: [],
  },
  {
    membership_id: "11111111-1111-4111-8111-000000000003",
    tenant_id: "aaaaaaaa-1111-4111-8111-000000000003",
    tenant_name: "Initech (no workspace)",
    role_name: "member",
    permissions: [],
  },
];

const workspace = (
  id: string,
  tenantId: string,
  name: string,
  createdAt: string,
): TenantWorkspaceRead => ({
  id,
  tenant_id: tenantId,
  name,
  description: `${name} workspace`,
  is_active: true,
  created_at: createdAt,
  updated_at: createdAt,
  workspace_role: "editor",
  permissions: [],
});

const ACME = MOCK_TENANT_MEMBERSHIPS[0].tenant_id;
const GLOBEX = MOCK_TENANT_MEMBERSHIPS[1].tenant_id;
const INITECH = MOCK_TENANT_MEMBERSHIPS[2].tenant_id;

/**
 * Keyed by tenant id, already in the order the backend promises: "Default"
 * first, then created_at ascending. Initech is present but empty — the
 * documented `200 []` response for a tenant with no workspace membership.
 */
/** A fake but well-formed JWT-shaped string — never decoded in mock mode. */
export const mockTenantAccessToken = (tenantId: string): string =>
  `mock-tenant-token.${tenantId}`;

export const MOCK_TENANT_WORKSPACES: Record<string, TenantWorkspaceRead[]> = {
  [ACME]: [
    workspace(
      "bbbbbbbb-1111-4111-8111-000000000001",
      ACME,
      "Default",
      "2026-01-05T10:00:00Z",
    ),
    workspace(
      "bbbbbbbb-1111-4111-8111-000000000002",
      ACME,
      "Marketing",
      "2026-02-11T10:00:00Z",
    ),
    workspace(
      "bbbbbbbb-1111-4111-8111-000000000003",
      ACME,
      "Sales",
      "2026-03-20T10:00:00Z",
    ),
  ],
  [GLOBEX]: [
    workspace(
      "bbbbbbbb-1111-4111-8111-000000000004",
      GLOBEX,
      "Default",
      "2026-01-08T10:00:00Z",
    ),
  ],
  [INITECH]: [],
};

const orgPickerMembership = (
  index: number,
  tenantName: string,
  roleName: string,
): MembershipRead => ({
  membership_id: `22222222-2222-4222-8222-00000000000${index}`,
  tenant_id: `cccccccc-2222-4222-8222-00000000000${index}`,
  tenant_name: tenantName,
  role_name: roleName,
  permissions: [],
});

/**
 * The auth org picker (C1–C3) in Storybook and tests. The first two are the
 * design references' organizations; all six cross the 5-organization search
 * threshold.
 */
export const MOCK_ORG_PICKER_MEMBERSHIPS: MembershipRead[] = [
  orgPickerMembership(1, "Hardik's Workspace", "tenant_admin"),
  orgPickerMembership(2, "udhayaprakash122's Workspace", "member"),
  orgPickerMembership(3, "Acme Analytics", "admin"),
  orgPickerMembership(4, "Globex Data", "viewer"),
  orgPickerMembership(5, "Initech Finance", "billing_manager"),
  orgPickerMembership(6, "Umbrella Research Collaborative", "member"),
];
